import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { Subject } from 'rxjs';
import { palette } from '../../theme/palette';
import { SEARCH_SHORT_PLACEHOLDER } from '../../constants/search';
import {
  SearchAutoSuggest,
  StyleSearchAutoSuggestIcon,
  SEARCH_AUTOSUGGEST_GLOBAL_STYLE_ID,
  searchAutoSuggestGlobalCss,
} from './EquipmentSearchAutoSuggestStyle';
import { useEffect, useMemo, useState } from 'react';

export interface SearchAutoSuggestProps {
  id: string;
  showCloseButton?: boolean;
  searchToggleId?: string;
  isMobileNavSearchElement?: boolean;
}

const closeMobileSearchBlock = new CustomEvent('closemobilesearchblock');

if (
  typeof document !== 'undefined' &&
  !document.getElementById(SEARCH_AUTOSUGGEST_GLOBAL_STYLE_ID)
) {
  const style = document.createElement('style');
  style.id = SEARCH_AUTOSUGGEST_GLOBAL_STYLE_ID;
  style.textContent = searchAutoSuggestGlobalCss;
  document.head.appendChild(style);
}

function EquipmentSearchAutoSuggest({
  id,
  showCloseButton,
  searchToggleId,
  isMobileNavSearchElement,
}: SearchAutoSuggestProps) {
  const [isActive, setIsActive] = useState<boolean>(false);
  const [input, setInput] = useState<string>('');

  const inputFocusSubject = useMemo(() => new Subject<boolean>(), []);
  const closeAutocompleteSubject = useMemo(() => new Subject<boolean>(), []);

  const initSearchElement = () => {
    const element = getPlaceholderElement();

    if (!element) return;

    document.addEventListener('click', (event) => {
      globalClickHandler(event.target, element);
    });
  };

  const globalClickHandler = (targetElement: EventTarget | null, element: HTMLElement | null) => {
    let targetEl: EventTarget | null = targetElement;
    let isClickedInside = false;

    if (!targetEl) return;

    const toggleElement = searchToggleId ? document.getElementById(searchToggleId) : null;

    do {
      if (targetEl == element || (toggleElement && targetEl == toggleElement)) {
        isClickedInside = true;
        break;
      }
      targetEl = (targetEl as HTMLElement).parentNode;
    } while (targetEl);

    if (!isClickedInside) {
      setIsActive(false);
    }
  };

  const getPlaceholderElement = () => {
    return document.getElementById(id);
  };

  const keyDownHandler = () => {
    setTimeout(() => {
      if (!document.activeElement) return;

      const element = getPlaceholderElement();

      if (element !== document.activeElement && !element?.contains(document.activeElement)) {
        setIsActive(false);
      }
    }, 0);
  };

  const openHandler = () => {
    setIsActive(true);
    inputFocusSubject.next(true);
  };

  const closeHandler = () => {
    setIsActive(false);
    closeAutocompleteSubject.next(true);

    if (isMobileNavSearchElement) {
      document.dispatchEvent(closeMobileSearchBlock);
    }
  };

  useEffect(() => {
    initSearchElement();
  }, []);

  useEffect(() => {
    if (!searchToggleId) return;

    const toggleElement = document.getElementById(searchToggleId);
    if (!toggleElement) return;

    const handleToggleClick = () => {
      setIsActive(true);
    };

    toggleElement.addEventListener('click', handleToggleClick);

    return () => {
      toggleElement.removeEventListener('click', handleToggleClick);
    };
  }, [searchToggleId]);

  useEffect(() => {
    if (!searchToggleId) return;

    const searchElement = getPlaceholderElement();
    const toggleElement = document.getElementById(searchToggleId);
    const container = searchElement?.parentElement;
    if (!searchElement || !toggleElement || !container) return;

    const measure = () => {
      const c = container.getBoundingClientRect();
      const t = toggleElement.getBoundingClientRect();
      const left = Math.max(0, Math.round(t.left - c.left));
      searchElement.style.setProperty('--es-search-toggle-left', `${left}px`);
      searchElement.style.setProperty('--es-search-expand-width', `${Math.round(c.width - left)}px`);
    };

    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [searchToggleId]);

  useEffect(() => {
    if (isActive) {
      openHandler();
    } else {
      closeHandler();
    }
  }, [isActive]);

  return (
    <SearchAutoSuggest
      onFocus={openHandler}
      onKeyDown={keyDownHandler}
      startIcon={<StyleSearchAutoSuggestIcon />}
      placeholder={SEARCH_SHORT_PLACEHOLDER}
      inputFocusSubject={inputFocusSubject}
      closeAutocompleteSubject={closeAutocompleteSubject}
      onInputChangeCallBack={setInput}
      endIcon={
        (showCloseButton && input) || isMobileNavSearchElement ? (
          <HighlightOffIcon onClick={closeHandler} style={{ color: palette.custom.grey }} />
        ) : undefined
      }
    />
  );
}

export default EquipmentSearchAutoSuggest;
